import { Check, Loader2, Mail } from 'lucide-react'
import type { ProposalRecipientDto } from '@lib/dto/proposal-delivery.dto'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

/**
 * Send a version to the client, and be clear about which kind of sending.
 *
 * The button used to say Send with a paper-plane icon and email nobody. An
 * admin marked a proposal sent, the client received nothing, and the UI gave
 * no reason to suspect a gap. Both things it can now do are therefore named
 * for what they actually do: one emails the proposal, the other records that
 * somebody sent it by hand.
 *
 * Emailing is the primary action when it is possible, and when it is not the
 * dialog says what is missing and who fixes it — a recipient discovered at the
 * point of sending is a recipient discovered too late.
 */
export function SendProposalDialog({
  open,
  onOpenChange,
  versionNumber,
  clientName,
  recipient,
  isEmailing,
  isMarking,
  onEmail,
  onMarkSent,
  emailError,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  versionNumber: number
  clientName: string
  recipient: ProposalRecipientDto | null
  isEmailing: boolean
  isMarking: boolean
  onEmail: () => void
  onMarkSent: () => void
  /** Set when emailing failed. The dialog is modal, so a message behind it is not visible. */
  emailError: string | null
}) {
  const canEmail = recipient !== null && recipient.email !== null
  const busy = isEmailing || isMarking

  function renderRecipient() {
    if (recipient === null) {
      return (
        <p className="rounded-md border border-default bg-subtle px-3 py-2 text-xs text-secondary-text">
          {clientName} has no primary contact, so there is nobody to email
          this to. Add a contact on the client and mark them primary — or send
          it yourself and record that below.
        </p>
      )
    }
    if (recipient.email === null) {
      return (
        <p className="rounded-md border border-default bg-subtle px-3 py-2 text-xs text-secondary-text">
          {recipient.name} has no email address recorded, so this cannot be
          emailed yet. Add one on the client — or send it yourself and record
          that below.
        </p>
      )
    }
    return (
      <p className="rounded-md border border-default bg-subtle px-3 py-2 text-xs text-secondary-text">
        It will be emailed to{' '}
        <span className="font-medium text-primary-text">{recipient.name}</span>{' '}
        at{' '}
        <span className="font-medium text-primary-text">{recipient.email}</span>
        , with a link to read it online. You will be able to see whether they
        opened it.
      </p>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Send version {versionNumber}?</DialogTitle>
          <DialogDescription>
            Sending freezes the document, so what {clientName} receives can
            never be rewritten by a later change to the survey.
          </DialogDescription>
        </DialogHeader>

        {renderRecipient()}

        {emailError === null ? null : (
          <p className="text-sm text-danger">{emailError}</p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => {
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>
          {/*
            Offered whatever happens: a proposal handed over at a meeting or
            printed and posted is still sent, and the record should say so.
            Secondary once emailing is possible, so the default is the one
            that actually delivers.
          */}
          <Button
            variant={canEmail ? 'outline' : 'default'}
            size="sm"
            disabled={busy}
            onClick={onMarkSent}
          >
            {isMarking ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Check className="h-3.5 w-3.5" />
            )}
            {canEmail ? 'Just mark it sent' : 'Mark as sent'}
          </Button>
          {canEmail ? (
            <Button size="sm" disabled={busy} onClick={onEmail}>
              {isEmailing ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Mail className="h-3.5 w-3.5" />
              )}
              Email it
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
